/**
 * Lykke in the top bar: the button (talk or type) and the panel under it. The panel is a window onto the one
 * conversation — closing it hides the window, not the conversation, and a running call keeps going while it is
 * closed (the button shows that she is listening or talking).
 */
import { useEffect, useRef } from 'react';
import { CornerUpLeft, Loader2, Mic, MicOff, X } from 'lucide-react';
import { cn } from '@/components/ui/utils';
import { AgentComposer, type CallPhase } from '@/cowork/shell/AgentComposer';
import { useGuide, type CallState } from './GuideContext';

const CALL_LABEL: Record<CallState, string> = { off: '', connecting: 'Connecting…', listening: 'Listening', thinking: 'On it', speaking: 'Talking' };
const WORKING: Record<string, string> = { find_campaigns: 'Looking up your campaigns', find_content: 'Looking through your content', find_my_work: 'Looking up your decks and videos' };

function Avatar({ live }: { live?: boolean }) {
  return (
    <span className="relative grid h-6 w-6 shrink-0 place-items-center rounded-full bg-gradient-to-br from-[#be43ff] to-[#7a1fbf] text-[11px] font-bold text-white">
      L
      {live && <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 animate-pulse rounded-full border-2 border-white bg-[#a9f600]" />}
    </span>
  );
}

/** The top-bar control: open the conversation, or start/stop talking. */
export function GuideButton() {
  const g = useGuide();
  if (!g?.available) return null;
  const live = g.call !== 'off';
  return (
    <div className="flex items-center gap-0.5 rounded-full border border-gray-200 bg-white p-0.5" data-guide-button>
      <button type="button" onClick={() => g.setOpen(!g.open)} aria-expanded={g.open} aria-label="Ask Lykke"
        className={cn('flex h-8 items-center gap-2 rounded-full pl-1 pr-3 text-[13px] font-semibold text-[#344054] transition-colors hover:bg-gray-50', g.open && 'bg-[#be43ff]/8 text-[#7a1fbf]')}>
        <Avatar live={live} />
        {live ? CALL_LABEL[g.call] : 'Ask Lykke'}
      </button>
      <button type="button" onClick={() => (live ? g.endCall() : void g.startCall())} aria-pressed={live} aria-label={live ? 'Stop talking to Lykke' : 'Talk to Lykke'}
        title={live ? 'Stop the call' : 'Talk instead of typing'}
        className={cn('grid h-8 w-8 place-items-center rounded-full transition-colors', live ? 'bg-[#101828] text-white hover:bg-[#344054]' : 'text-[#667085] hover:bg-gray-50 hover:text-[#7a1fbf]')}>
        {g.call === 'connecting' ? <Loader2 className="h-4 w-4 animate-spin" /> : live ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
      </button>
    </div>
  );
}

/** The guide's conversation: the thread (navigations with Undo, the yes/no before an action) and the universal composer. */
export function GuideConversation({ className, autoFocus }: { className?: string; autoFocus?: boolean }) {
  const g = useGuide();
  const end = useRef<HTMLDivElement>(null);
  useEffect(() => { end.current?.scrollIntoView({ block: 'end' }); }, [g?.messages.length, g?.busy, g?.confirmAsk]);
  if (!g?.available) return null;
  const callPhase: CallPhase = g.call === 'off' ? 'off' : g.call;
  return (
    <div className={cn('flex min-h-0 flex-col', className)}>
      <div className="flex min-h-[120px] flex-1 flex-col gap-2 overflow-y-auto px-4 py-3" data-guide-thread>
        {!g.messages.length && (
          <div className="flex flex-col gap-1.5 py-1 text-[13px] text-[#667085]">
            {['Results of my latest campaign', 'Slides with the results of my last 12 campaigns', 'Open the Content Hub'].map((s) => (
              <button key={s} type="button" onClick={() => void g.send(s, 'chat')} className="self-start rounded-full border border-gray-200 px-3 py-1 text-left text-[12.5px] text-[#344054] hover:border-[#be43ff]/40 hover:bg-[#be43ff]/5">{s}</button>
            ))}
          </div>
        )}
        {g.messages.map((m) => m.role === 'user' ? (
          <div key={m.id} className="max-w-[85%] self-end rounded-2xl rounded-br-md bg-[var(--shell-bubble,#f2f4f7)] px-3 py-1.5 text-[13.5px] text-[var(--shell-text,#101828)]">
            {m.via === 'voice' && <Mic className="mr-1 inline h-3 w-3 -translate-y-px text-[#98a2b3]" />}{m.text}
          </div>
        ) : m.role === 'nav' ? (
          <div key={m.id} className="flex items-center gap-2 self-start text-[12px] text-[#667085]" data-guide-nav>
            <span className={cn('rounded-full px-2 py-0.5 font-semibold', m.back ? 'bg-[#f2f4f7] text-[#344054]' : 'bg-[#a9f600]/30 text-[#3b5a00]')}>{m.back ? `Back to ${m.text}` : `Opened ${m.text}`}</span>
            {m.from && <button type="button" onClick={() => g.undo(m.from!)} className="inline-flex items-center gap-0.5 font-semibold text-[#7a1fbf] hover:underline"><CornerUpLeft className="h-3 w-3" />Undo</button>}
          </div>
        ) : m.role === 'error' ? (
          <div key={m.id} className="self-start text-[12.5px] text-[#b42318]">{m.text}</div>
        ) : (
          <div key={m.id} className="max-w-[92%] self-start text-[13.5px] leading-snug text-[var(--shell-text,#101828)]" data-guide-say>{m.text}</div>
        ))}
        {g.busy && !g.confirmAsk && (
          <div className="flex items-center gap-1.5 self-start text-[12px] text-[#98a2b3]"><Loader2 className="h-3 w-3 animate-spin" />{(g.working && WORKING[g.working]) || (g.heading ? `Opening ${g.heading}…` : 'On it')}</div>
        )}
        {g.confirmAsk && (
          <div className="flex flex-col gap-2 self-stretch rounded-xl border border-[#be43ff]/30 bg-[#be43ff]/5 p-3" data-guide-confirm>
            <span className="text-[13.5px] font-semibold text-[var(--shell-text,#101828)]">{g.confirmAsk.text}</span>
            <div className="flex gap-2">
              <button type="button" onClick={() => g.answerConfirm(true)} className="rounded-full bg-[#101828] px-3.5 py-1 text-[12.5px] font-semibold text-white hover:bg-[#344054]">Yes</button>
              <button type="button" onClick={() => g.answerConfirm(false)} className="rounded-full border border-gray-300 px-3.5 py-1 text-[12.5px] font-semibold text-[#344054] hover:bg-gray-50">No</button>
            </div>
          </div>
        )}
        <div ref={end} />
      </div>
      <AgentComposer
        className="mx-2.5 mb-2.5"
        agentName="Lykke"
        allowFiles={false}
        autoFocus={autoFocus}
        placeholder="Where to, or what to make?"
        onSend={(t) => { void g.send(t, 'chat'); }}
        sendWhileBusy
        call={{ phase: callPhase, start: () => void g.startCall(), end: () => g.endCall(), caption: g.call !== 'off' ? 'Talk, she moves the app for you.' : undefined }}
      />
    </div>
  );
}

/** The conversation window under the top bar (classic layout). */
export function GuidePanel() {
  const g = useGuide();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && g?.open) g.setOpen(false); if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k' && g?.available) { e.preventDefault(); g.setOpen(true); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [g]);
  if (!g?.available || !g.open) return null;

  return (
    <section aria-label="Lykke" data-guide-panel
      className="fixed right-4 top-[60px] z-[60] flex max-h-[min(72vh,660px)] w-[380px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl">
      <header className="flex items-center gap-2 border-b border-gray-100 px-4 py-2.5">
        <Avatar live={g.call !== 'off'} />
        <div className="flex min-w-0 flex-1 flex-col leading-tight">
          <span className="text-[13.5px] font-semibold text-[#101828]">Lykke</span>
          <span className="text-[11.5px] text-[#667085]">{g.call !== 'off' ? CALL_LABEL[g.call] : 'Say where to go or what to make'}</span>
        </div>
        <button type="button" onClick={() => g.setOpen(false)} aria-label="Close" className="grid h-7 w-7 place-items-center rounded-full text-[#667085] hover:bg-gray-100"><X className="h-4 w-4" /></button>
      </header>
      <GuideConversation className="flex-1" autoFocus />
    </section>
  );
}
